import React, {useState} from "react"
import {Box, Typography, Popover, Button, CircularProgress} from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import styled from "styled-components"
import {useNavigate, useLocation} from "react-router-dom"

import {api} from "@indebted/api"
import type {CollectionHold} from "@indebted/api"
import {colors} from "@indebted/theme/colors"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {ConfirmationDialog} from "@indebted/components/ConfirmationDialog"

type CollectionHoldsBannerProps = {
	customerID: string
	holds: CollectionHold[]
	refreshing?: boolean
	reload: () => void
}

function CollectionHoldsBanner({customerID, holds, refreshing, reload}: CollectionHoldsBannerProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [releasing, setReleasing] = useState<string | null>(null)
	const [confirmHoldID, setConfirmHoldID] = useState<string | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const [agentEmail] = useAgent()
	const {notification} = useNotification()

	if (!refreshing && holds.length === 0) return null

	const handleRelease = (holdID: string) => {
		setConfirmHoldID(null)
		setReleasing(holdID)
		api.customer
			.releaseCollectionHold(customerID, holdID, {AgentEmail: agentEmail})
			.then(() => {
				setReleasing(null)
				setAnchorEl(null)
				notification.success("Hold released successfully.")
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setReleasing(null)
				notification.error(error.Message)
			})
	}

	return (
		<>
			<ConfirmationDialog
				description="Are you sure you want to release this hold?"
				isOpen={confirmHoldID !== null}
				onCancel={() => setConfirmHoldID(null)}
				onOK={() => confirmHoldID && handleRelease(confirmHoldID)}
			/>
			<BannerChip onClick={refreshing ? undefined : (e) => setAnchorEl(e.currentTarget)}>
				{refreshing ? (
					<CircularProgress size={14} sx={{color: colors.warning["700"]}} />
				) : (
					<WarningAmberIcon sx={{fontSize: 16, color: colors.warning["700"]}} />
				)}
				<Typography variant="body2" fontWeight={600} color={colors.warning["800"]} noWrap>
					{refreshing ? "Updating..." : `Collection On Hold (${holds.length})`}
				</Typography>
			</BannerChip>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{vertical: "bottom", horizontal: "left"}}
				transformOrigin={{vertical: "top", horizontal: "left"}}
				slotProps={{paper: {sx: {borderRadius: "8px", mt: 0.5, minWidth: 280}}}}
			>
				<Box p={2}>
					<Typography variant="subtitle2" fontWeight={600} mb={1}>
						Collection On Hold
					</Typography>
					<Box display="flex" flexDirection="column" gap={1.5}>
						{holds.map((hold) => (
							<HoldItem key={hold.HoldID}>
								<Typography variant="body2">{hold.ReasonDisplay}</Typography>
								{hold.ReferenceType === "CUSTOMER_CASE" && hold.ReferenceID && (
									<Button
										size="small"
										variant="outlined"
										onClick={() => {
											setAnchorEl(null)
											const params = new URLSearchParams(location.search)
											params.set("CaseID", hold.ReferenceID!)
											navigate(`/customers/${customerID}/cases?${params.toString()}`)
										}}
										sx={{
											textTransform: "none",
											fontSize: "0.75rem",
											fontWeight: 600,
											color: colors.warmBlue["900"],
											p: "2px 8px",
											minWidth: "auto",
										}}
									>
										See Case
									</Button>
								)}
								{hold.ManuallyReleasable && (
									<Button
										size="small"
										variant="outlined"
										disabled={releasing === hold.HoldID}
										onClick={() => setConfirmHoldID(hold.HoldID)}
										sx={{
											textTransform: "none",
											fontSize: "0.75rem",
											fontWeight: 600,
											color: colors.warning["800"],
											borderColor: colors.warning["300"],
											p: "2px 8px",
											minWidth: "auto",
											"&:hover": {
												borderColor: colors.warning["500"],
												backgroundColor: colors.warning["50"],
											},
										}}
									>
										{releasing === hold.HoldID ? "Releasing..." : "Release"}
									</Button>
								)}
							</HoldItem>
						))}
					</Box>
				</Box>
			</Popover>
		</>
	)
}

const BannerChip = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	background-color: ${colors.warning["50"]};
	border: 1px solid ${colors.warning["200"]};
	border-radius: 6px;
	padding: 4px 12px;
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		background-color: ${colors.warning["100"]};
	}
`

const HoldItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 6px 8px;
	border-radius: 4px;
	background-color: ${colors.gray["50"]};
`

export {CollectionHoldsBanner}
